import { useEffect, useRef, useState } from "react";

const LAUNCH_STAGES = [
  "Opening your private circle...",
  "Warming the garden path...",
  "Finding today's shared rhythm...",
  "Setting the table for two..."
] as const;

export interface BrownianMotionState {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  targetVelocityX: number;
  targetVelocityY: number;
  secondsUntilTurn: number;
}

interface BrownianBounds {
  maxX: number;
  maxY: number;
}

const BROWNIAN_MIN_SPEED = 38;
const BROWNIAN_MAX_SPEED = 86;

/**
 * Advances one frame of a correlated random walk. Velocity eases toward a
 * frequently changing random heading, while edge reflections keep the bug
 * inside the visible loading canvas.
 */
export function advanceBrownianMotion(
  state: BrownianMotionState,
  bounds: BrownianBounds,
  elapsedSeconds: number,
  random: () => number = Math.random
): BrownianMotionState {
  const delta = Math.min(Math.max(elapsedSeconds, 0), 0.05);
  let secondsUntilTurn = state.secondsUntilTurn - delta;
  let targetVelocityX = state.targetVelocityX;
  let targetVelocityY = state.targetVelocityY;

  if (secondsUntilTurn <= 0) {
    const currentHeading = Math.atan2(state.velocityY, state.velocityX);
    const nextHeading = currentHeading + (random() - 0.5) * Math.PI * 1.8;
    const nextSpeed = BROWNIAN_MIN_SPEED + random() * (BROWNIAN_MAX_SPEED - BROWNIAN_MIN_SPEED);
    targetVelocityX = Math.cos(nextHeading) * nextSpeed;
    targetVelocityY = Math.sin(nextHeading) * nextSpeed;
    secondsUntilTurn = 0.32 + random() * 0.78;
  }

  const steering = 1 - Math.exp(-2.8 * delta);
  let velocityX = state.velocityX + (targetVelocityX - state.velocityX) * steering;
  let velocityY = state.velocityY + (targetVelocityY - state.velocityY) * steering;

  // Small independent impulses keep the path alive between larger turns.
  velocityX += (random() - 0.5) * 18 * delta;
  velocityY += (random() - 0.5) * 18 * delta;

  const speed = Math.hypot(velocityX, velocityY);
  if (speed > BROWNIAN_MAX_SPEED) {
    const scale = BROWNIAN_MAX_SPEED / speed;
    velocityX *= scale;
    velocityY *= scale;
  }

  const maxX = Math.max(bounds.maxX, 0);
  const maxY = Math.max(bounds.maxY, 0);
  let x = state.x + velocityX * delta;
  let y = state.y + velocityY * delta;

  if (x <= 0) {
    x = 0;
    velocityX = Math.abs(velocityX);
    targetVelocityX = Math.abs(targetVelocityX);
    secondsUntilTurn = Math.min(secondsUntilTurn, 0.24);
  } else if (x >= maxX) {
    x = maxX;
    velocityX = -Math.abs(velocityX);
    targetVelocityX = -Math.abs(targetVelocityX);
    secondsUntilTurn = Math.min(secondsUntilTurn, 0.24);
  }

  if (y <= 0) {
    y = 0;
    velocityY = Math.abs(velocityY);
    targetVelocityY = Math.abs(targetVelocityY);
    secondsUntilTurn = Math.min(secondsUntilTurn, 0.24);
  } else if (y >= maxY) {
    y = maxY;
    velocityY = -Math.abs(velocityY);
    targetVelocityY = -Math.abs(targetVelocityY);
    secondsUntilTurn = Math.min(secondsUntilTurn, 0.24);
  }

  return {
    x,
    y,
    velocityX,
    velocityY,
    targetVelocityX,
    targetVelocityY,
    secondsUntilTurn
  };
}

function createBrownianMotion(bounds: BrownianBounds, random: () => number): BrownianMotionState {
  const heading = random() * Math.PI * 2;
  const speed = BROWNIAN_MIN_SPEED + random() * (BROWNIAN_MAX_SPEED - BROWNIAN_MIN_SPEED);

  return {
    x: random() * bounds.maxX,
    y: random() * bounds.maxY,
    velocityX: Math.cos(heading) * speed,
    velocityY: Math.sin(heading) * speed,
    targetVelocityX: Math.cos(heading) * speed,
    targetVelocityY: Math.sin(heading) * speed,
    secondsUntilTurn: 0.32 + random() * 0.78
  };
}

function BrownianLadybug() {
  const fieldRef = useRef<HTMLSpanElement>(null);
  const ladybugRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const field = fieldRef.current;
    const ladybug = ladybugRef.current;
    if (!field || !ladybug || typeof window.requestAnimationFrame !== "function") return;

    const motionPreference = typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)")
      : undefined;
    let animationFrame: number | undefined;
    let previousTimestamp: number | undefined;
    let motion: BrownianMotionState | undefined;
    let disposed = false;

    const animate = (timestamp: number) => {
      if (disposed || motionPreference?.matches) {
        animationFrame = undefined;
        return;
      }

      const fieldWidth = field.clientWidth;
      const fieldHeight = field.clientHeight;
      if (fieldWidth === 0 || fieldHeight === 0) {
        animationFrame = window.requestAnimationFrame(animate);
        return;
      }

      const bounds = {
        maxX: Math.max(fieldWidth - (ladybug.offsetWidth || 34), 0),
        maxY: Math.max(fieldHeight - (ladybug.offsetHeight || 34), 0)
      };
      motion ??= createBrownianMotion(bounds, Math.random);

      const elapsedSeconds = previousTimestamp === undefined
        ? 1 / 60
        : (timestamp - previousTimestamp) / 1_000;
      previousTimestamp = timestamp;
      motion = advanceBrownianMotion(motion, bounds, elapsedSeconds);

      const heading = Math.atan2(motion.velocityY, motion.velocityX) * (180 / Math.PI) + 90;
      ladybug.style.transform = `translate3d(${motion.x.toFixed(2)}px, ${motion.y.toFixed(2)}px, 0) rotate(${heading.toFixed(2)}deg)`;
      animationFrame = window.requestAnimationFrame(animate);
    };

    const stop = () => {
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = undefined;
      }
    };

    const start = () => {
      if (disposed || motionPreference?.matches || animationFrame !== undefined) return;
      previousTimestamp = undefined;
      animationFrame = window.requestAnimationFrame(animate);
    };

    const handleMotionPreference = () => {
      stop();
      if (motionPreference?.matches) {
        ladybug.style.removeProperty("transform");
      } else {
        start();
      }
    };

    motionPreference?.addEventListener("change", handleMotionPreference);
    start();

    return () => {
      disposed = true;
      stop();
      motionPreference?.removeEventListener("change", handleMotionPreference);
    };
  }, []);

  return (
    <span aria-hidden="true" className="launch-brownian-field" ref={fieldRef}>
      <span
        className="launch-brownian-ladybug"
        data-testid="launch-brownian-ladybug"
        ref={ladybugRef}
      >
        <img alt="" src="/companions/tiny-creatures/ladybug.png" />
      </span>
    </span>
  );
}

/** Full-bleed launch/loading surface shared by boot Suspense and connected loading. */
export function LaunchSplash({
  copy
}: {
  copy?: string;
}) {
  const [stageIndex, setStageIndex] = useState(0);
  const stageCopy = copy ?? LAUNCH_STAGES[stageIndex];

  useEffect(() => {
    if (copy !== undefined) return;
    const interval = window.setInterval(() => {
      setStageIndex((current) => (current + 1) % LAUNCH_STAGES.length);
    }, 1_450);
    return () => window.clearInterval(interval);
  }, [copy]);

  return (
    <main className="launch-screen" role="status" aria-live="polite" data-testid="launch-splash">
      <BrownianLadybug />
      <div className="launch-mark" aria-hidden="true">
        <span className="launch-orbit-ring launch-orbit-ring-one" />
        <span className="launch-orbit-ring launch-orbit-ring-two" />
        <img alt="" className="launch-creature" src="/companions/tiny-creatures/ladybug.png" />
        <span className="launch-leaf launch-leaf-one" />
        <span className="launch-leaf launch-leaf-two" />
      </div>
      <strong className="launch-brand">Ladybug</strong>
      <span className="launch-copy" key={stageCopy}>{stageCopy}</span>
      <span className="launch-progress" aria-hidden="true">
        <span style={{ width: `${((stageIndex + 1) / LAUNCH_STAGES.length) * 100}%` }} />
      </span>
      <span className="launch-motion-note">Your shared garden is waking up.</span>
    </main>
  );
}
